import { useEffect, useState, useCallback } from "react";
import { RefreshCw, Loader2, Eye, EyeOff, Copy, ChevronDown, PhoneCall, Search, Gem, ShieldAlert, Euro, Handshake, CalendarCheck, BookOpen, MessageSquareText } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";
import { FASE_STYLE, MOMENTO_LABEL, OBIEZIONE_LABEL, formatDate } from "../lib/constants";

const ICONS = { apertura: PhoneCall, scoperta_bisogni: Search, presentazione_valore: Gem, gestione_obiezioni: ShieldAlert, gestione_prezzo: Euro, chiusura: Handshake, follow_up: CalendarCheck };

export default function ScriptManual() {
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(false);
  const [memo, setMemo] = useState(false);
  const [open, setOpen] = useState({});
  const [revealed, setRevealed] = useState({});
  const [showLinee, setShowLinee] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/manual"); setM(data); }
    catch (e) { toast.error(errorMessage(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Frase copiata"); };
  const toggleMemo = () => { setMemo(!memo); setRevealed({}); };

  if (!m) return <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 spin-slow" /> Caricamento…</div>;

  return (
    <div data-testid="manual-page" className="space-y-8">
      <div className="fade-up flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Da ripassare</p>
          <h1 className="mt-2 text-4xl font-bold tracking-tight sm:text-5xl">Manuale script</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">Gli script della tua KB per fase → intento → cluster. Le linee-guida (coaching) sono in una sezione separata.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {m.stato === "done" && (
            <button data-testid="manual-memo-toggle" onClick={toggleMemo} className={`inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-medium transition-colors ${memo ? "border-[#F5B942]/50 bg-[#F5B942]/15 text-[#FCD990]" : "border-border text-muted-foreground hover:text-white"}`}>
              {memo ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />} Modalità memoria
            </button>
          )}
          <button data-testid="manual-regenerate-btn" onClick={load} disabled={loading} className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:border-primary hover:text-white disabled:opacity-40">
            <RefreshCw className={`h-4 w-4 ${loading ? "spin-slow" : ""}`} /> Aggiorna
          </button>
        </div>
      </div>

      {m.stato === "empty" && <div data-testid="manual-empty" className="surface p-10 text-center text-sm text-muted-foreground">La knowledge base è vuota: carica contenuti per generare il manuale.</div>}

      {m.stato === "done" && (
        <>
          <p className="text-xs font-mono text-muted-foreground">{m.n_script} script · {m.n_linee} linee-guida · aggiornato {formatDate(m.aggiornato_al)}</p>
          {memo && <p data-testid="manual-memo-hint" className="rounded-lg bg-[#F5B942]/10 p-3 text-xs text-[#FCD990]">Modalità memoria: leggi la situazione, prova a dire la frase a voce, poi tocca per verificarla.</p>}

          <div className="space-y-4">
            {m.fasi.map((f, si) => {
              const Icon = ICONS[f.fase] || BookOpen; const st = FASE_STYLE[f.fase]; const isOpen = open[f.fase] ?? false;
              return (
                <section key={f.fase} data-testid={`manual-section-${f.fase}`} className="surface overflow-hidden">
                  <button data-testid={`manual-section-toggle-${f.fase}`} aria-expanded={isOpen} onClick={() => setOpen({ ...open, [f.fase]: !isOpen })} className="flex w-full items-center gap-4 p-5 text-left">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg" style={{ background: st.bg, color: st.text }}><Icon className="h-5 w-5" /></span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2 text-base font-semibold md:text-lg"><span className="font-mono text-xs text-muted-foreground">{si + 1}.</span>{f.label}<span className="font-mono text-xs text-muted-foreground">({f.n_frasi})</span></span>
                      <span className="block text-xs text-muted-foreground">{f.descrizione}</span>
                    </span>
                    <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                  {isOpen && (
                    <div className="space-y-5 border-t border-border p-4 sm:p-5">
                      {f.intenti.length === 0 && <p className="text-sm text-muted-foreground">Nessuno script per questa fase. Carica contenuti su questo tema.</p>}
                      {f.intenti.map((it, ii) => (
                        <div key={ii}>
                          <div className="mb-2 flex items-center gap-2">
                            <span className="text-sm font-semibold" style={{ color: st.text }}>{it.label}</span>
                            {it.stato === "da_confermare" && <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[11px] text-amber-300">da confermare</span>}
                          </div>
                          <ol className="space-y-2">
                            {it.clusters.map((c, i) => {
                              const key = `${si}-${ii}-${i}`; const hidden = memo && !revealed[key];
                              return (
                                <li key={key} data-testid={`manual-phrase-${f.fase}-${ii}-${i}`} className="group rounded-xl bg-background p-4">
                                  <div className="mb-1 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
                                    <span className="font-mono uppercase tracking-wide">{c.titolo}</span>
                                    {c.momento && c.momento !== "trasversale" && <span className="rounded-full border border-border px-1.5">{MOMENTO_LABEL[c.momento]}</span>}
                                    {c.obiezione_tag && <span className="rounded-full border border-red-500/30 bg-red-500/10 px-1.5 text-red-300">{OBIEZIONE_LABEL[c.obiezione_tag]}</span>}
                                  </div>
                                  <div className="flex items-start gap-3">
                                    <span className="mt-0.5 font-mono text-xs" style={{ color: st.text }}>{i + 1}</span>
                                    {hidden ? (
                                      <button data-testid={`manual-reveal-${f.fase}-${ii}-${i}`} onClick={() => setRevealed({ ...revealed, [key]: true })} className="flex-1 rounded-lg border border-dashed border-[#F5B942]/40 px-4 py-3 text-left text-sm text-[#FCD990] hover:bg-[#F5B942]/10">Tocca per rivelare la frase</button>
                                    ) : (
                                      <p className="flex-1 text-base font-medium leading-relaxed">{c.frase_rappresentativa}</p>
                                    )}
                                    {!hidden && <button data-testid={`manual-copy-${f.fase}-${ii}-${i}`} onClick={() => copy(c.frase_rappresentativa)} className="rounded-md p-1.5 text-muted-foreground hover:bg-secondary hover:text-white"><Copy className="h-3.5 w-3.5" /></button>}
                                  </div>
                                  {!hidden && c.varianti?.length > 0 && (
                                    <ul className="mt-2 space-y-1 border-l-2 border-border pl-6">
                                      {c.varianti.map((v, vi) => <li key={vi} className="text-sm text-gray-400">{v}</li>)}
                                    </ul>
                                  )}
                                </li>
                              );
                            })}
                          </ol>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              );
            })}
          </div>

          {m.linee_guida?.length > 0 && (
            <section data-testid="manual-guidelines" className="surface overflow-hidden">
              <button data-testid="manual-guidelines-toggle" onClick={() => setShowLinee(!showLinee)} className="flex w-full items-center gap-4 p-5 text-left">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-secondary text-muted-foreground"><MessageSquareText className="h-5 w-5" /></span>
                <span className="min-w-0 flex-1">
                  <span className="text-base font-semibold md:text-lg">Linee-guida (coaching)</span>
                  <span className="block text-xs text-muted-foreground">Istruzioni di comportamento, non da pronunciare al cliente</span>
                </span>
                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${showLinee ? "rotate-180" : ""}`} />
              </button>
              {showLinee && (
                <div className="space-y-4 border-t border-border p-4 sm:p-5">
                  {m.linee_guida.map((g) => (
                    <div key={g.fase}>
                      <p className="mb-2 text-sm font-semibold" style={{ color: FASE_STYLE[g.fase].text }}>{g.label}</p>
                      <ul className="space-y-2">
                        {g.clusters.map((c) => (
                          <li key={c.id} data-testid={`guideline-${c.id}`} className="rounded-lg bg-background p-3 text-sm">
                            <div className="flex items-start gap-2 text-gray-300">
                              <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: FASE_STYLE[g.fase].bar }} />
                              <span>{c.frase_rappresentativa}</span>
                            </div>
                            {c.frase_pronta && (
                              <div className="mt-2 flex items-start gap-2 rounded-md border border-[#F5B942]/25 bg-[#F5B942]/10 p-2.5">
                                <MessageSquareText className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#FCD990]" />
                                <div className="flex-1">
                                  <p className="text-[10px] font-mono uppercase tracking-wide text-[#FCD990]">Frase pronta</p>
                                  <p className="text-sm text-white">{c.frase_pronta}</p>
                                </div>
                                <button data-testid={`guideline-copy-${c.id}`} onClick={() => copy(c.frase_pronta)} className="rounded-md p-1 text-muted-foreground hover:bg-secondary hover:text-white"><Copy className="h-3.5 w-3.5" /></button>
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
}
